import React from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'
import SVGIcon from 'reusableComponents/SVGIcon'
import { ReactComponent as PenToSquare } from 'assets/svg/pen-to-square-fa.svg'
import { ReactComponent as HouseIcon } from 'assets/svg/house-building-fa.svg'
import { ReactComponent as MessageLine } from 'assets/svg/Message-lg.svg'
import { colors, breakpoints } from 'globalStyles'
import { ReactComponent as GearIcon } from 'assets/svg/gear-black.svg'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { selectDeliverySummary, selectSignatureInfo } from 'store/shipping/shippingSelectors'
import constants from 'constants.json'
import PackagePreference from './PackagePreference'

const StyledPopUp = styled(Modal)`
  .modal-dialog.modal-dialog-centered {
    justify-content: center;
  }
  .modal-content {
    max-width: 330px;
    @media ${breakpoints.desktop} {
      max-width: 500px;
    }
  }
  .modal-header {
    background-color: ${colors.translucent_light_blue};
    padding: 0px;
    height: 75px;
    justify-content: center;
  }
  .modal-body {
    text-align: center;
    padding: 24px 16px;
  }
  .modal-footer {
    border-top: none;
    justify-content: center;
    margin: 0px 16px 24px;
    padding: 0px;
  }
  .modal-title {
    font-weight: 600;
    font-size: 20px;
    padding: 15px;
  }
  .title-text {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-weight: 600;
    font-size: 20px;
  }
`
export const gearIconStyle = {
  paddingRight: '12px',
  width: '45px',
  height: '45px',
}

export const titleStyle = {
  paddingRight: '12px',
  height: '27px',
}

const StyledButton = styled.button`
  width: 298px;
  @media ${breakpoints.desktop} {
    width: 468px;
  }
  height: 50px;
  background-color: ${colors.primary_white};
  color: ${colors.primary_blue};
  border: 1px solid ${colors.primary_blue};
  border-radius: 4px;
  &:hover {
    color: ${colors.text_link_hover};
    border-color: ${colors.text_link_hover};
  }
`
const getSignatureRequirements = (signature, allowWaiveSignature, t) => {
  const SIGNATURE_REQUIRED = constants.SIGNATURE.REQUIRED
  const SIGNATURE_WAIVED = constants.SIGNATURE.WAIVED
  let signaturePref = t(`deliveryPreferencesSummary.delivery_opt.none`)

  if (signature === SIGNATURE_REQUIRED) {
    if (!allowWaiveSignature) {
      signaturePref = t('deliveryPreferencesSummary.no_sign_waive')
    } else {
      signaturePref = t('deliveryPreferencesSummary.sign_required')
    }
  } else if (signature === SIGNATURE_WAIVED) {
    signaturePref = t('deliveryPreferencesSummary.sign_waived')
  }
  return signaturePref
}

const getPreferencesList = (
  t,
  deliveryLocation,
  additionalDeliveryInstructions,
  signature,
  allowWaiveSignature
) => [
  {
    key: 'signature',
    title: t(`set_preferences.waive_sign`),
    icon: PenToSquare,
    iconStyle: { width: '18px', height: '18px' },
    text: getSignatureRequirements(signature, allowWaiveSignature, t),
  },
  {
    key: 'delivery_location',
    title: t(`set_preferences.where_to_leave`),
    icon: HouseIcon,
    iconStyle: { width: '18px', height: '14px' },
    text: deliveryLocation
      ? t(`deliveryPreferencesSummary.delivery_opt.${deliveryLocation}`)
      : t(`deliveryPreferencesSummary.delivery_opt.none`),
  },
  {
    key: 'additional_instructions',
    title: t(`set_preferences.add_del_instructions`),
    icon: MessageLine,
    iconStyle: { width: '18px', height: '18px' },
    text: additionalDeliveryInstructions || t(`deliveryPreferencesSummary.delivery_opt.none`),
  },
]

const PackagePreferencesModal = ({ show, onClose }) => {
  const { t } = useTranslation()

  const { deliveryLocation, additionalDeliveryInstructions } = useSelector(selectDeliverySummary)

  const { signature, allowWaiveSignature } = useSelector(selectSignatureInfo)

  const onClickClose = () => {
    onClose()
  }

  return (
    <StyledPopUp show={show} backdrop="static" centered>
      <StyledPopUp.Header>
        <StyledPopUp.Title autoFocus>
          <div className="title-text">
            <SVGIcon Icon={GearIcon} size="lg" style={gearIconStyle} />
            <div tabIndex="-1" style={titleStyle}>
              {t('package_preferences.package_preferences')}
            </div>
          </div>
        </StyledPopUp.Title>
      </StyledPopUp.Header>
      <StyledPopUp.Body>
        <PackagePreference
          preferenceItems={getPreferencesList(
            t,
            deliveryLocation,
            additionalDeliveryInstructions,
            signature,
            allowWaiveSignature
          )}
        />
      </StyledPopUp.Body>
      <StyledPopUp.Footer>
        <StyledButton onClick={onClickClose}>{t('buttonActions.close')}</StyledButton>
      </StyledPopUp.Footer>
    </StyledPopUp>
  )
}

PackagePreferencesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
}

PackagePreferencesModal.defaultProps = {
  onClose: null,
}

export default PackagePreferencesModal
